import { isTestAccountEmail } from "@/lib/community/curation";

/**
 * Company domains whose users are Panda's own team, not real customers. Traffic
 * from these addresses should be excludable from the upgrade / monetization
 * funnel so internal QA walkthroughs never distort conversion rates.
 */
const INTERNAL_EMAIL_DOMAINS = ["bidx.ai", "panda.new"];

/**
 * A plain-language statement of what isInternalEmail() excludes, for any report
 * that shows numbers with these accounts removed and needs to say so. Built from
 * the team-domain list above so that half cannot drift; the throwaway-domain and
 * test-address halves live in isTestAccountEmail (lib/community/curation.ts) —
 * update the wording here if those rules change.
 */
export const INTERNAL_ACCOUNTS_DESCRIPTION =
  `team (${INTERNAL_EMAIL_DOMAINS.map((d) => `@${d}`).join(", ")}), ` +
  `throwaway domains (yopmail, mailinator…) and test-style addresses (test+, qa.…)`;

/**
 * True when an email belongs to an internal / test user: our own team domains
 * OR the throwaway / QA / e2e conventions already recognised by
 * `isTestAccountEmail` (test+tags, "qa", "playwright", mailinator, …).
 *
 * Pure string logic — safe on both client and server. Used to stamp an
 * `is_internal` marker on people (client, via buildPostHogPersonProperties) and
 * on server-side monetization events, so PostHog funnels can filter internal
 * traffic out with a single condition.
 */
export function isInternalEmail(email?: string | null): boolean {
  if (!email) return false;
  const e = email.trim().toLowerCase();
  if (INTERNAL_EMAIL_DOMAINS.some((d) => e.endsWith(`@${d}`))) return true;
  return isTestAccountEmail(e);
}
